import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "@/lib/i18n";
import { YACHTS } from "@/data/content";
import { SectionHead } from "./Reveal";
import { useBooking } from "./BookingContext";

const YachtItem = ({ y, i, active, onEnter }) => {
  const { t, lang } = useLang();
  const { choose } = useBooking();
  return (
    <motion.div onMouseEnter={onEnter}
      initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.9, delay: i * 0.08 }}
      className={`group border-t border-white/10 py-8 md:py-10 transition-colors duration-500 ${active ? "border-[#C5A059]/60" : ""}`}
      data-testid={`yacht-item-${y.id}`}>
      <div className="lg:hidden mb-6 aspect-[16/10] overflow-hidden rounded-2xl">
        <img src={y.img} alt={y.title[lang]} loading="lazy" className="h-full w-full object-cover" />
      </div>
      <div className="flex items-start gap-6">
        <span className="font-display text-xl text-[#C5A059] pt-2">{String(i + 1).padStart(2, "0")}</span>
        <div className="flex-1">
          <div className="flex flex-wrap items-baseline justify-between gap-3">
            <h3 className={`font-display font-light text-4xl md:text-5xl transition-colors duration-500 ${active ? "text-[#F3E5AB]" : "text-[#F9F9F9]"}`}>{y.title[lang]}</h3>
            <span className="text-[11px] uppercase tracking-[0.25em] text-zinc-400">{t.duration[y.dur]}</span>
          </div>
          <p className="mt-4 max-w-md text-zinc-300">{y.desc[lang]}</p>
          <p className="mt-4 text-sm text-zinc-300" data-testid={`yacht-price-${y.id}`}>
            <span className="text-xs uppercase tracking-[0.2em] text-zinc-400 mr-2">{t.tours.from}</span>
            <span className="font-display text-3xl text-[#E5C158]">{y.price.toLocaleString("pt-PT")} €</span>
          </p>
          <button onClick={() => choose(y.id)} data-testid={`yacht-book-${y.id}`}
            className="mt-6 inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#E5C158] link-underline">
            {t.yachts.book}<ArrowUpRight size={14} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export const Yachts = () => {
  const { t, lang } = useLang();
  const [active, setActive] = useState(0);
  return (
    <section id="iates" className="relative py-28 md:py-40 bg-[#0E0E12]" data-testid="yachts-section">
      <div className="mx-auto max-w-[1440px] px-5 md:px-10 grid lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-6 lg:order-2">
          <SectionHead over={t.yachts.over} lines={t.yachts.title} sub={t.yachts.sub} testid="yachts-heading" />
          <div className="mt-14">
            {YACHTS.map((y, i) => <YachtItem key={y.id} y={y} i={i} active={active === i} onEnter={() => setActive(i)} />)}
          </div>
        </div>
        <div className="hidden lg:block lg:col-span-6 lg:order-1">
          <div className="sticky top-24 h-[78vh] overflow-hidden rounded-[32px] border border-[#C5A059]/25">
            {YACHTS.map((y, i) => (
              <motion.img key={y.id} src={y.img} alt={y.title[lang]}
                animate={{ opacity: active === i ? 1 : 0, scale: active === i ? 1 : 1.08 }}
                transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
                className="absolute inset-0 h-full w-full object-cover" />
            ))}
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0C]/70 via-transparent to-transparent" />
            <p className="absolute bottom-8 left-8 font-display italic text-3xl text-[#F3E5AB]">Rio Tejo</p>
          </div>
        </div>
      </div>
    </section>
  );
};
